// Central i18n dictionary. German (de) is the default language, English (en)
// is selectable. No component hardcodes user-facing copy — everything routes
// through these objects via the useI18n() hook.

export type Lang = "de" | "en";

interface Dict {
  nav: { home: string; library: string; jam: string; myJams: string };
  common: {
    startJam: string;
    viewAll: string;
    guidance: string;
    bpm: string;
    tempo: string;
    home: string;
    experimental: string;
  };
  langName: { de: string; en: string };
  home: {
    brand: string;
    heroTitle: string; // may contain a newline
    heroPara: string;
    myJams: string;
    training: string;
    recentSessions: string;
    emptyTakes: string;
    profileLine: string; // {level} {mode} {zones}
    trainingCards: { t: string; s: string }[];
  };
  library: {
    kicker: string;
    header: string;
    sub: string;
    recommended: string;
    continueTitle: string;
    trainingTitle: string;
    prepare: string;
    keepPlaying: string;
    focusLabel: string;
    emptyTakes: string;
    customTitle: string;
    customText: string;
    comingSoon: string;
    training: { t: string; s: string }[];
  };
  prepare: {
    kicker: string; // "Prepare · {style}"
    meta: string; // "{key} · {bpm} BPM · {a}/{b}"
    needs: string;
    tapToHear: string; // "{zone} · tap to hear"
    quickPractice: string;
    skipJam: string;
  };
  session: {
    title: string;
    subtitle: string;
    stats: { flow: string; transitions: string; recovery: string; comfort: string };
    feel: string;
    placeholder: string;
    addToSpace: string;
    saved: string;
    thisTake: string;
    continueTake: string;
    saveVersion: string;
    newVariation: string;
    freshJam: string;
    discard: string;
    defaultVersion: string;
    toastNeedWords: string;
    toastAddedSpace: string;
    toastSaved: string; // {name}
    toastVariation: string;
    toastDiscarded: string;
  };
  myjams: {
    kicker: string;
    title: string;
    desc: string;
    empty: string;
    startJam: string;
    variation: string; // "var {n}"
    resume: string;
    rename: string;
    duplicate: string;
    delete: string;
    toastRenamed: string;
    toastDuplicated: string;
    toastDeleted: string;
    untitled: string;
  };
  mic: { test: string; voiceNote: string; listening: string; error: string };
  dev: {
    title: string;
    nextSection: string;
    upcoming: string;
    stableFlow: string;
    recovery1: string;
    recovery2: string;
    recovery3: string;
    clearRecovery: string;
  };
  modes: { FOLLOW: string; FLOW: string; FREE: string };
  messages: Record<string, string>;
  sections: Record<string, string>;
  difficulty: Record<string, string>;
  styles: Record<string, string>;
  keys: Record<string, string>;
  progress: Record<string, string>;
  flow: Record<string, string>;
  levels: Record<string, string>;
  songs: Record<string, { needs: string[]; focus: string }>;
}

const de: Dict = {
  nav: { home: "START", library: "BIBLIOTHEK", jam: "JAM", myJams: "MEINE JAMS" },
  common: {
    startJam: "JAM STARTEN",
    viewAll: "ALLE ANZEIGEN",
    guidance: "Führung",
    bpm: "BPM",
    tempo: "Tempo",
    home: "START",
    experimental: "experimentell",
  },
  langName: { de: "Deutsch", en: "Englisch" },
  home: {
    brand: "Adaptive Jam",
    heroTitle: "Dein ruhiger\nJam-Partner.",
    heroPara:
      "Höre den Track, sieh, wo du sicher spielen kannst, spüre den Wechsel, bevor er kommt – und wenn du den Faden verlierst, holen wir dich sanft zurück.",
    myJams: "Meine Jams",
    training: "Training",
    recentSessions: "Letzte Sessions",
    emptyTakes: "Hier erscheinen deine gespeicherten Spuren. Beende einen Jam und speichere eine Version.",
    profileLine: "Level: {level} · Bevorzugt {mode} · Vertraut in {zones}",
    trainingCards: [
      { t: "a-Moll-Pentatonik", s: "Position 1 · den Grundton verankern" },
      { t: "Ruhige Positionswechsel", s: "Ohne Panik zwischen den Boxen wechseln" },
      { t: "Recovery-Reflex", s: "Finde deinen Grundton in einem Schlag" },
    ],
  },
  library: {
    kicker: "Backing-Tracks",
    header: "Was möchtest du spielen?",
    sub: "Wähle einen vorbereiteten Jam oder arbeite an einer deiner gespeicherten Versionen weiter.",
    recommended: "Empfohlen für dich",
    continueTitle: "Deine letzten Spuren",
    trainingTitle: "Trainings-Jams",
    prepare: "VORBEREITEN",
    keepPlaying: "WEITERSPIELEN",
    focusLabel: "Fokus",
    emptyTakes: "Noch keine gespeicherten Spuren. Beende einen Jam und speichere eine Version.",
    customTitle: "Eigenen Song verwenden",
    customText: "Später kannst du der App einen eigenen Song vorspielen oder eine Audiodatei analysieren lassen.",
    comingSoon: "KOMMT SPÄTER",
    training: [
      { t: "Puls finden", s: "Halte einen einfachen Groove mit sehr wenigen Noten." },
      { t: "Grundton finden", s: "Lerne, zu einem sicheren Anker-Ton zurückzukehren." },
      { t: "Erster Wechsel", s: "Wechsle ruhig von einem Bereich des Griffbretts zum nächsten." },
    ],
  },
  prepare: {
    kicker: "Vorbereitung · {style}",
    meta: "{key} · {bpm} BPM · {a}/{b}",
    needs: "Dieser Song braucht",
    tapToHear: "{zone} · zum Hören tippen",
    quickPractice: "KURZ ÜBEN",
    skipJam: "ÜBERSPRINGEN & JAMMEN",
  },
  session: {
    title: "SESSION BEENDET",
    subtitle: "Du hast weitergespielt. Genau darum geht es.",
    stats: { flow: "Flow", transitions: "Wechsel", recovery: "Recovery", comfort: "Komfortzone" },
    feel: "WIE HAT SICH DAS ANGEFÜHLT?",
    placeholder: "Erzähl mir, wo du dich wohl oder verloren gefühlt hast.",
    addToSpace: "IN MEINEN COGNITIVE SPACE ÜBERNEHMEN",
    saved: "GESPEICHERT",
    thisTake: "Diese Jam-Spur",
    continueTake: "AN DIESER SPUR WEITERARBEITEN",
    saveVersion: "VERSION SPEICHERN",
    newVariation: "NEUE VARIANTE",
    freshJam: "NEUER JAM",
    discard: "VERWERFEN",
    defaultVersion: "Meine Version",
    toastNeedWords: "Schreib zuerst ein paar Worte – wie hat es sich angefühlt?",
    toastAddedSpace: "Zu deinem Cognitive Space hinzugefügt",
    toastSaved: "„{name}“ in Meine Jams gespeichert",
    toastVariation: "Neue Variante erstellt",
    toastDiscarded: "Session verworfen",
  },
  myjams: {
    kicker: "Dein Journal",
    title: "Meine Jams",
    desc: "Gespeicherte Spuren bleiben lokal im Browser erhalten – auch nach dem Neuladen.",
    empty: "Noch keine Spuren.",
    startJam: "JAM STARTEN",
    variation: "Var. {n}",
    resume: "Fortsetzen",
    rename: "Umbenennen",
    duplicate: "Duplizieren",
    delete: "Löschen",
    toastRenamed: "Umbenannt",
    toastDuplicated: "Dupliziert",
    toastDeleted: "Gelöscht",
    untitled: "Ohne Titel",
  },
  mic: {
    test: "Eingang testen",
    voiceNote: "Sprachnotiz",
    listening: "Höre zu",
    error: "Mikrofon nicht verfügbar oder Zugriff verweigert",
  },
  dev: {
    title: "Simulations-Deck",
    nextSection: "→ Nächster Abschnitt",
    upcoming: "→ Kommender Wechsel",
    stableFlow: "→ Stabiler Flow",
    recovery1: "Recovery · Stufe 1 (Grundton)",
    recovery2: "Recovery · Stufe 2 (2 Töne)",
    recovery3: "Recovery · Stufe 3 (3 Töne)",
    clearRecovery: "Recovery beenden",
  },
  modes: { FOLLOW: "FÜHRUNG", FLOW: "FLOW", FREE: "FREI" },
  messages: {
    STAY: "BLEIB HIER",
    LISTEN: "HÖR ZU",
    HOLD: "HALTEN",
    READY: "GLEICH",
    MOVE: "WECHSEL",
    OPEN: "FREI",
    "YOU'RE IN": "DU BIST DRIN",
    LOST: "ICH BIN RAUS",
    END: "ENDE",
  },
  sections: {
    Intro: "Intro",
    Verse: "Strophe",
    Build: "Aufbau",
    Chorus: "Refrain",
    "Verse 2": "Strophe 2",
    Outro: "Outro",
  },
  difficulty: { Gentle: "Sanft", Fluid: "Fließend", Energetic: "Energisch" },
  styles: { All: "Alle", "Neo-Soul": "Neo-Soul", "Slow Blues": "Slow Blues", "Rock Ballad": "Rock-Ballade" },
  keys: { "A minor": "a-Moll" },
  progress: { Working: "In Arbeit", Saved: "Gespeichert", Fresh: "Neu" },
  flow: { DEEP: "TIEF", GOOD: "GUT", STEADY: "STABIL" },
  levels: { Hobbyist: "Hobby-Gitarrist" },
  songs: {
    "amber-drift": {
      focus: "Groove + Pentatonik Position 1",
      needs: [
        "a-Moll-Pentatonik – Position 1",
        "Ein ruhiger Positionswechsel zu Position 2",
        "Ein entspannter 2-Ton-Groove",
        "Gleichmäßiger Viertel-Puls",
      ],
    },
    "midnight-groove": {
      focus: "Feeling + Bendings am Grundton",
      needs: [
        "a-Moll-Pentatonik – Position 1",
        "Selbstbewusste Bendings um den Grundton",
        "Ein Positionswechsel für den Turnaround",
        "Spüre den Backbeat",
      ],
    },
    "velvet-minor": {
      focus: "Gehaltene Töne + Positionswechsel",
      needs: [
        "a-Moll-Pentatonik – Position 1 & 2",
        "Größere, gehaltene Grundtöne",
        "Hebe für den Refrain in Position 2",
        "Lass die Akkorde atmen",
      ],
    },
  },
};

const en: Dict = {
  nav: { home: "HOME", library: "LIBRARY", jam: "JAM", myJams: "MY JAMS" },
  common: {
    startJam: "START JAM",
    viewAll: "VIEW ALL",
    guidance: "Guidance",
    bpm: "BPM",
    tempo: "Tempo",
    home: "HOME",
    experimental: "experimental",
  },
  langName: { de: "German", en: "English" },
  home: {
    brand: "Adaptive Jam",
    heroTitle: "Your calm\njam partner.",
    heroPara:
      "Hear the track, see where you can safely play, feel the change before it arrives — and if you lose your place, we gently bring you back.",
    myJams: "My Jams",
    training: "Training",
    recentSessions: "Recent Sessions",
    emptyTakes: "Your saved takes will live here. Finish a jam and save a version.",
    profileLine: "Level: {level} · Prefers {mode} · Comfortable in {zones}",
    trainingCards: [
      { t: "A minor pentatonic", s: "Position 1 · anchor the root" },
      { t: "Calm position shifts", s: "Move between boxes without panic" },
      { t: "Recovery reflex", s: "Find your root in one beat" },
    ],
  },
  library: {
    kicker: "Backing Tracks",
    header: "What do you want to play?",
    sub: "Pick a prepared jam or keep working on one of your saved versions.",
    recommended: "Recommended for you",
    continueTitle: "Your recent takes",
    trainingTitle: "Training Jams",
    prepare: "PREPARE",
    keepPlaying: "KEEP PLAYING",
    focusLabel: "Focus",
    emptyTakes: "No saved takes yet. Finish a jam and save a version.",
    customTitle: "Use your own song",
    customText: "Later you'll be able to play the app your own song or have it analyse an audio file.",
    comingSoon: "COMING SOON",
    training: [
      { t: "Find the pulse", s: "Keep a simple groove using very few notes." },
      { t: "Find the root", s: "Learn to return to one safe anchor note." },
      { t: "First shift", s: "Move calmly from one fretboard area to another." },
    ],
  },
  prepare: {
    kicker: "Prepare · {style}",
    meta: "{key} · {bpm} BPM · {a}/{b}",
    needs: "This song needs",
    tapToHear: "{zone} · tap to hear",
    quickPractice: "QUICK PRACTICE",
    skipJam: "SKIP & JAM",
  },
  session: {
    title: "SESSION COMPLETE",
    subtitle: "You kept playing. That's the whole point.",
    stats: { flow: "Flow", transitions: "Transitions", recovery: "Recovery", comfort: "Comfort Zone" },
    feel: "HOW DID THAT FEEL?",
    placeholder: "Tell me where you felt comfortable or lost.",
    addToSpace: "ADD TO COGNITIVE SPACE",
    saved: "SAVED",
    thisTake: "This Jam Take",
    continueTake: "CONTINUE THIS TAKE",
    saveVersion: "SAVE VERSION",
    newVariation: "NEW VARIATION",
    freshJam: "FRESH JAM",
    discard: "DISCARD",
    defaultVersion: "My Version",
    toastNeedWords: "Write a few words first — how did it feel?",
    toastAddedSpace: "Added to your Cognitive Space",
    toastSaved: '"{name}" saved to My Jams',
    toastVariation: "New variation created",
    toastDiscarded: "Session discarded",
  },
  myjams: {
    kicker: "Your Journal",
    title: "My Jams",
    desc: "Saved takes persist locally in your browser and survive refresh.",
    empty: "No takes yet.",
    startJam: "START A JAM",
    variation: "var {n}",
    resume: "Resume",
    rename: "Rename",
    duplicate: "Duplicate",
    delete: "Delete",
    toastRenamed: "Renamed",
    toastDuplicated: "Duplicated",
    toastDeleted: "Deleted",
    untitled: "Untitled",
  },
  mic: {
    test: "Test your input",
    voiceNote: "Voice note",
    listening: "Listening",
    error: "Microphone unavailable or permission denied",
  },
  dev: {
    title: "Simulation Deck",
    nextSection: "→ Next section",
    upcoming: "→ Upcoming transition",
    stableFlow: "→ Stable flow",
    recovery1: "Recovery · level 1 (root)",
    recovery2: "Recovery · level 2 (2 notes)",
    recovery3: "Recovery · level 3 (3 notes)",
    clearRecovery: "Clear recovery",
  },
  modes: { FOLLOW: "FOLLOW", FLOW: "FLOW", FREE: "FREE" },
  messages: {
    STAY: "STAY",
    LISTEN: "LISTEN",
    HOLD: "HOLD",
    READY: "READY",
    MOVE: "MOVE",
    OPEN: "OPEN",
    "YOU'RE IN": "YOU'RE IN",
    LOST: "LOST",
    END: "END",
  },
  sections: {
    Intro: "Intro",
    Verse: "Verse",
    Build: "Build",
    Chorus: "Chorus",
    "Verse 2": "Verse 2",
    Outro: "Outro",
  },
  difficulty: { Gentle: "Gentle", Fluid: "Fluid", Energetic: "Energetic" },
  styles: { All: "All", "Neo-Soul": "Neo-Soul", "Slow Blues": "Slow Blues", "Rock Ballad": "Rock Ballad" },
  keys: { "A minor": "A minor" },
  progress: { Working: "Working", Saved: "Saved", Fresh: "Fresh" },
  flow: { DEEP: "DEEP", GOOD: "GOOD", STEADY: "STEADY" },
  levels: { Hobbyist: "Hobbyist" },
  songs: {
    "amber-drift": {
      focus: "Groove + pentatonic Position 1",
      needs: [
        "A minor pentatonic — Position 1",
        "One calm position shift to Position 2",
        "A relaxed 2-note groove",
        "Steady quarter-note pulse",
      ],
    },
    "midnight-groove": {
      focus: "Feel + bends around the root",
      needs: [
        "A minor pentatonic — Position 1",
        "Confident bends around the root",
        "One position move for the turnaround",
        "Feel the back-beat",
      ],
    },
    "velvet-minor": {
      focus: "Sustained notes + position shift",
      needs: [
        "A minor pentatonic — Position 1 & 2",
        "Bigger sustained root notes",
        "Lift into Position 2 for the chorus",
        "Let the chords breathe",
      ],
    },
  },
};

export const translations: Record<Lang, Dict> = { de, en };
export type { Dict };
